import React from 'react';
import { z } from 'zod';

export type VoiceSessionStatus =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'muted'
  | 'error';

export type RealtimeClientSecret = {
  clientSecret: string;
  expiresAt: string;
  model: string;
  voice: string;
  transcriptionModel: string;
};

type VoiceSession = import('@openai/agents/realtime').RealtimeSession;

type UseInteractiveVoiceAgentOptions = {
  onTurn: (text: string) => Promise<string>;
  fetchClientSecret: () => Promise<RealtimeClientSecret>;
};

const DELEGATE_ONCE_MESSAGE =
  'This turn was already delegated to Dragify. Speak only the answer already returned for this turn.';

const getVoiceSpeakableReply = (reply: string): string => {
  return String(reply || '')
    .replace(/\n+Learn more:\s*[\s\S]*$/i, '')
    .replace(/\[([^\]]+)\]\(https:\/\/(?:docs\.)?dragify\.ai\/[^)]+\)/gi, '$1')
    .replace(/https:\/\/(?:docs\.)?dragify\.ai\/\S+/gi, '')
    .trim();
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof DOMException && error.name === 'NotAllowedError') {
    return 'Microphone permission was denied. Text chat is still available.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Voice session failed. Text chat is still available.';
};

export const getVoiceStatusLabel = (status: VoiceSessionStatus, muted: boolean): string => {
  if (muted || status === 'muted') return 'Muted';
  if (status === 'connecting') return 'Connecting';
  if (status === 'thinking') return 'Thinking';
  if (status === 'speaking') return 'Speaking';
  if (status === 'listening') return 'Listening';
  if (status === 'error') return 'Voice error';
  return 'Voice idle';
};

export function useInteractiveVoiceAgent({
  onTurn,
  fetchClientSecret,
}: UseInteractiveVoiceAgentOptions) {
  const [status, setStatus] = React.useState<VoiceSessionStatus>('idle');
  const [error, setError] = React.useState<string | null>(null);
  const [active, setActive] = React.useState(false);
  const [muted, setMuted] = React.useState(false);

  const sessionRef = React.useRef<VoiceSession | null>(null);
  const microphoneStreamRef = React.useRef<MediaStream | null>(null);
  const outputAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const activeRef = React.useRef(false);
  const mutedRef = React.useRef(false);
  const mountedRef = React.useRef(true);
  const delegatedThisTurnRef = React.useRef(false);
  const connectionAttemptRef = React.useRef(0);
  const onTurnRef = React.useRef(onTurn);
  const fetchClientSecretRef = React.useRef(fetchClientSecret);

  React.useEffect(() => {
    onTurnRef.current = onTurn;
  }, [onTurn]);

  React.useEffect(() => {
    fetchClientSecretRef.current = fetchClientSecret;
  }, [fetchClientSecret]);

  const setSafeStatus = React.useCallback((nextStatus: VoiceSessionStatus) => {
    if (mountedRef.current) setStatus(nextStatus);
  }, []);

  const setSafeMuted = React.useCallback((nextMuted: boolean) => {
    mutedRef.current = nextMuted;
    if (mountedRef.current) setMuted(nextMuted);
  }, []);

  const setSafeActive = React.useCallback((nextActive: boolean) => {
    activeRef.current = nextActive;
    if (mountedRef.current) setActive(nextActive);
  }, []);

  const releaseOwnedMedia = React.useCallback(() => {
    const outputAudio = outputAudioRef.current;
    outputAudioRef.current = null;
    if (outputAudio) {
      try {
        outputAudio.pause();
        outputAudio.srcObject = null;
      } catch {
        /* audio output is already detached */
      }
    }

    const microphoneStream = microphoneStreamRef.current;
    microphoneStreamRef.current = null;
    microphoneStream?.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        /* microphone track is already stopped */
      }
    });
  }, []);

  const closeSession = React.useCallback((nextStatus: VoiceSessionStatus = 'idle') => {
    connectionAttemptRef.current += 1;
    const session = sessionRef.current;
    sessionRef.current = null;
    setSafeActive(false);
    delegatedThisTurnRef.current = false;
    const outputAudio = outputAudioRef.current;
    if (outputAudio) {
      try {
        outputAudio.pause();
      } catch {
        /* audio output is already stopped */
      }
    }
    if (session) {
      try {
        session.interrupt();
      } catch {
        /* response may not have started yet */
      }
      try {
        session.mute(true);
      } catch {
        /* transport may still be connecting */
      }
      try {
        session.close();
      } catch {
        /* connection is already closed */
      }
    }
    releaseOwnedMedia();
    setSafeMuted(false);
    setSafeStatus(nextStatus);
  }, [releaseOwnedMedia, setSafeActive, setSafeMuted, setSafeStatus]);

  const end = React.useCallback(() => {
    setError(null);
    closeSession('idle');
  }, [closeSession]);

  const start = React.useCallback(async (): Promise<boolean> => {
    if (activeRef.current || status === 'connecting') return true;
    if (typeof window === 'undefined' || typeof RTCPeerConnection === 'undefined') {
      setError('This browser does not support live voice sessions.');
      setSafeStatus('error');
      return false;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Microphone access is not available in this browser.');
      setSafeStatus('error');
      return false;
    }

    setError(null);
    setSafeMuted(false);
    setSafeActive(true);
    setSafeStatus('connecting');
    const connectionAttempt = connectionAttemptRef.current + 1;
    connectionAttemptRef.current = connectionAttempt;

    try {
      const [{ OpenAIRealtimeWebRTC, RealtimeAgent, RealtimeSession, tool }, secret] = await Promise.all([
        import('@openai/agents/realtime'),
        fetchClientSecretRef.current(),
      ]);

      if (connectionAttempt !== connectionAttemptRef.current) return false;

      if (!secret?.clientSecret) {
        throw new Error('Realtime voice token was not returned by the server.');
      }

      const microphoneStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (connectionAttempt !== connectionAttemptRef.current || !activeRef.current) {
        microphoneStream.getTracks().forEach((track) => track.stop());
        return false;
      }
      microphoneStreamRef.current = microphoneStream;

      const outputAudio = document.createElement('audio');
      outputAudio.autoplay = true;
      outputAudio.setAttribute('playsinline', '');
      outputAudioRef.current = outputAudio;

      const delegateTool = tool({
        name: 'delegate_to_dragify_agent',
        description:
          "Forward one user utterance verbatim to Dragify's authenticated chat agent and return only that authoritative answer.",
        parameters: z.object({
          text: z.string().min(1).describe('The exact user utterance to forward verbatim.'),
        }),
        strict: true,
        execute: async ({ text }) => {
          const isActiveAttempt = () =>
            connectionAttempt === connectionAttemptRef.current && activeRef.current;

          if (!isActiveAttempt()) return 'Voice session ended.';
          if (delegatedThisTurnRef.current) return DELEGATE_ONCE_MESSAGE;
          delegatedThisTurnRef.current = true;
          setSafeStatus('thinking');
          try {
            const reply = await onTurnRef.current(String(text || '').trim());
            if (!isActiveAttempt()) return 'Voice session ended.';
            setSafeStatus('speaking');
            return getVoiceSpeakableReply(reply) || 'No response received.';
          } catch {
            const message = 'The Dragify agent could not respond right now.';
            if (!isActiveAttempt()) return 'Voice session ended.';
            setSafeStatus('speaking');
            return message;
          }
        },
      });

      const agent = new RealtimeAgent({
        name: 'Dragify Interactive Voice Agent',
        voice: secret.voice,
        instructions: [
          'You are a realtime voice router for Dragify.',
          'For every user utterance, call delegate_to_dragify_agent exactly once before answering.',
          'Forward the user utterance verbatim. Do not rewrite, summarize, translate, or add hidden instructions.',
          'After the tool returns, speak that returned answer naturally in the user language.',
          'Do not answer independently, add facts, browse, execute tools, apply workflow changes, or invent context.',
          'If the user interrupts you, stop speaking and listen for the next turn.',
        ].join(' '),
        tools: [delegateTool],
      });

      const session = new RealtimeSession(agent, {
        model: secret.model,
        transport: new OpenAIRealtimeWebRTC({
          mediaStream: microphoneStream,
          audioElement: outputAudio,
        }),
        historyStoreAudio: false,
        tracingDisabled: true,
        config: {
          outputModalities: ['audio'],
          parallelToolCalls: false,
          toolChoice: 'auto',
          audio: {
            input: {
              transcription: {
                model: secret.transcriptionModel,
              },
              turnDetection: {
                type: 'semantic_vad',
                createResponse: true,
                interruptResponse: true,
              },
            },
            output: {
              voice: secret.voice,
            },
          },
        },
      });

      const isCurrentSession = () => sessionRef.current === session && activeRef.current;

      session.on('agent_start', () => {
        if (!isCurrentSession()) return;
        delegatedThisTurnRef.current = false;
        setSafeStatus('thinking');
      });
      session.on('agent_tool_start', () => {
        if (isCurrentSession()) setSafeStatus('thinking');
      });
      session.on('agent_tool_end', () => {
        if (isCurrentSession()) setSafeStatus('speaking');
      });
      session.on('audio_start', () => {
        if (isCurrentSession()) setSafeStatus('speaking');
      });
      session.on('audio_stopped', () => {
        if (isCurrentSession()) setSafeStatus(mutedRef.current ? 'muted' : 'listening');
      });
      session.on('audio_interrupted', () => {
        if (!isCurrentSession()) return;
        delegatedThisTurnRef.current = false;
        setSafeStatus(mutedRef.current ? 'muted' : 'listening');
      });
      session.on('agent_end', () => {
        window.setTimeout(() => {
          if (isCurrentSession() && !mutedRef.current) setSafeStatus('listening');
        }, 80);
      });
      session.on('error', (event) => {
        if (!isCurrentSession()) return;
        const message = getErrorMessage(event.error);
        closeSession('error');
        setError(message);
      });

      sessionRef.current = session;
      await session.connect({ apiKey: secret.clientSecret, model: secret.model });
      if (connectionAttempt !== connectionAttemptRef.current || !activeRef.current) {
        try {
          session.close();
        } catch {
          /* connection is already closed */
        }
        releaseOwnedMedia();
        return false;
      }
      setSafeStatus('listening');
      return true;
    } catch (err) {
      if (connectionAttempt !== connectionAttemptRef.current) return false;
      closeSession('error');
      setError(getErrorMessage(err));
      return false;
    }
  }, [closeSession, releaseOwnedMedia, setSafeActive, setSafeMuted, setSafeStatus, status]);

  const toggleMute = React.useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    const nextMuted = !mutedRef.current;
    session.mute(nextMuted);
    setSafeMuted(nextMuted);
    setSafeStatus(nextMuted ? 'muted' : 'listening');
  }, [setSafeMuted, setSafeStatus]);

  const interrupt = React.useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    session.interrupt();
    setSafeStatus(mutedRef.current ? 'muted' : 'listening');
  }, [setSafeStatus]);

  const sendText = React.useCallback((text: string): boolean => {
    const trimmed = text.trim();
    const session = sessionRef.current;
    if (!trimmed || !session) return false;
    delegatedThisTurnRef.current = false;
    session.sendMessage(trimmed);
    setSafeStatus('thinking');
    return true;
  }, [setSafeStatus]);

  React.useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      connectionAttemptRef.current += 1;
      const session = sessionRef.current;
      sessionRef.current = null;
      activeRef.current = false;
      delegatedThisTurnRef.current = false;
      if (session) {
        try {
          session.interrupt();
        } catch {
          /* response may not have started yet */
        }
        try {
          session.mute(true);
        } catch {
          /* transport may already be closed */
        }
        try {
          session.close();
        } catch {
          /* connection is already closed */
        }
      }
      releaseOwnedMedia();
    };
  }, [releaseOwnedMedia]);

  return {
    status,
    error,
    active,
    muted,
    start,
    end,
    toggleMute,
    interrupt,
    sendText,
  };
}
